/**
 * Platform Provider
 *
 * React context that provides platform-specific services to the app.
 * Note: Auth is now handled by AuthProvider - this provides other platform services.
 *
 * The context object and hooks live in `PlatformContext.ts` so this file
 * stays "components only" and is Fast-Refresh friendly.
 */

import React, { useEffect, useMemo } from 'react';
import { bridge } from '../bridge';
import { getPushPlatform } from './push';
import { getStoragePlatform } from './storage';
import { getNavigationPlatform } from './navigation';
import { getHapticsPlatform } from './haptics';
import { getPurchasePlatform } from './purchase';
import { getReviewPlatform } from './review';
import { PlatformContext, type PlatformContextValue } from './PlatformContext';

interface PlatformProviderProps {
  children: React.ReactNode;
}

export function PlatformProvider({ children }: PlatformProviderProps) {
  const value = useMemo<PlatformContextValue>(() => ({
    isNative: bridge.isNative,
    platform: bridge.platform,
    push: getPushPlatform(),
    storage: getStoragePlatform(),
    navigation: getNavigationPlatform(),
    haptics: getHapticsPlatform(),
    purchase: getPurchasePlatform(),
    review: getReviewPlatform(),
  }), []);

  // Marks the document as running inside the Flutter shell. The only consumer
  // today is the overscroll rule in index.css: the web build stops the
  // document's rubber-band (which drags the fixed nav bar with it), but the
  // native shell's pull-to-refresh is a UIRefreshControl on that same scroll
  // view and needs the top bounce to stay.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.toggle('native-shell', value.isNative);
  }, [value.isNative]);

  return (
    <PlatformContext.Provider value={value}>
      {children}
    </PlatformContext.Provider>
  );
}
